import {
  Box,
  createStyles,
  Grid,
  Group,
  NavLink,
  Stack,
  Text,
} from '@mantine/core';
import React, { ReactNode, useRef, useState } from 'react';

import { DashboardContentLayout } from '@portals/framework';
import { ReactComponent as List } from '@portals/icons/linear/list.svg';

import { DevCenterSection } from './DeviceCenterSection';
import DeviceSelector from './DeviceSelector';
import GetCommand from './GetCommand';
import GetConfig from './GetConfig';
import GetDevice from './GetDevice';
import GetLicenses from './GetLicenses';
import { Register } from './register/Register';
import SendDump from './SendDump';
import SendTelemetry from './SendTelemetry';
import SetConfig from './SetConfig';
import UpdateCommand from './UpdateCommand';
import UpdateDevice from './UpdateDevice';
import UpdateLicense from './UpdateLicense';

interface SectionItem {
  id: string;
  title: string;
  subtitle: string;
  docsLink?: string;
  component: ReactNode;
}

const SECTIONS: SectionItem[] = [
  {
    id: 'register-device',
    title: 'Register Device',
    subtitle: 'Device registration API',
    docsLink: 'https://dev.xyte.io/reference/register-device',
    component: <Register />,
  },
  {
    id: 'set-managed-device',
    title: 'Set managed device',
    subtitle:
      'Set the device ID and access key to be used in all following APIs',
    component: <DeviceSelector />,
  },
  {
    id: 'send-telemetry',
    title: 'Send Telemetry',
    subtitle: 'Send telemetry data',
    docsLink: 'https://dev.xyte.io/reference/send-telemetry',
    component: <SendTelemetry />,
  },
  {
    id: 'get-command',
    title: 'Get Command',
    subtitle: 'Get details of pending command',
    docsLink: 'https://dev.xyte.io/reference/get-command',
    component: <GetCommand />,
  },
  {
    id: 'update-command',
    title: 'Update Command',
    subtitle: 'Update server on command execution status',
    docsLink: 'https://dev.xyte.io/reference/update-command',
    component: <UpdateCommand />,
  },
  {
    id: 'update-device',
    title: 'Update Device',
    subtitle: 'Update device information',
    docsLink: 'https://dev.xyte.io/reference/update-device',
    component: <UpdateDevice />,
  },
  {
    id: 'get-device-info',
    title: 'Get Device Info',
    subtitle: 'Fetch device configuration from server',
    docsLink: 'https://dev.xyte.io/reference/get-device-info',
    component: <GetDevice />,
  },
  {
    id: 'get-config',
    title: 'Get Config',
    subtitle: 'Get latest device configuration from the server',
    docsLink: 'https://dev.xyte.io/reference/get-config',
    component: <GetConfig />,
  },
  {
    id: 'set-config',
    title: 'Set Config',
    subtitle: 'Send updated configuration to server',
    docsLink: 'https://dev.xyte.io/reference/set-config',
    component: <SetConfig />,
  },
  {
    id: 'send-dump',
    title: 'Send Dump',
    subtitle: 'Send a detailed report of device internals to the server',
    docsLink: 'https://dev.xyte.io/reference/send-dump',
    component: <SendDump />,
  },
  {
    id: 'get-licenses',
    title: 'Get Licenses',
    subtitle: 'Get pending license changed from the server',
    docsLink: 'https://dev.xyte.io/reference/get-licenses',
    component: <GetLicenses />,
  },
  {
    id: 'update-license',
    title: 'Update License',
    subtitle: 'Update server on license state',
    docsLink: 'https://dev.xyte.io/reference/update-license',
    component: <UpdateLicense />,
  },
];

export function DevCenter() {
  const { classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<HTMLDivElement[]>([]);
  const [activeSection, setActiveSection] = useState('');

  return (
    <DashboardContentLayout
      pageTitle="Dev Center"
      subtitle="Developer Test Tool"
    >
      <Grid className={classes.gridContainer} ref={containerRef}>
        <Grid.Col span={9}>
          <Stack spacing={32}>
            {SECTIONS.map(
              ({ id, title, subtitle, docsLink, component }, index) => (
                <DevCenterSection
                  key={id}
                  ref={(el: HTMLDivElement) =>
                    (sectionRefs.current[index] = el)
                  }
                  id={id}
                  title={title}
                  subtitle={subtitle}
                  docsLink={docsLink}
                >
                  {component}
                </DevCenterSection>
              )
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={3}>
          <Box className={classes.navigationContainer}>
            <Group>
              <List width={16} height={16} />
              <Text>Table of contents</Text>
            </Group>
            {SECTIONS.map(({ id, title }, index) => (
              <NavLink
                key={id}
                active={id === activeSection}
                label={title}
                onClick={() => {
                  setActiveSection(id);
                  sectionRefs.current[index].scrollIntoView({
                    behavior: 'smooth',
                  });
                }}
              />
            ))}
          </Box>
        </Grid.Col>
      </Grid>
    </DashboardContentLayout>
  );
}

const useStyles = createStyles(() => ({
  gridContainer: {
    maxWidth: 1000,
  },
  navigationContainer: {
    position: 'sticky',
    top: -30,
  },
}));
